/**
 * Settings container: invite form + paginated Stytch roster for the session org.
 */

import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { APIError } from '@localagent/sdk';
import type { InviteMode, InvitePolicy, OrgMember } from '@localagent/sdk';
import { getSDK } from '../../services/sdk-instance';
import { claimsAtom, holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { InviteMemberFormSection } from './InviteMemberFormSection';
import { OrganizationInvitePolicyForm } from './OrganizationInvitePolicyForm';
import { OrganizationRoster } from './OrganizationRoster';

export function OrganizationAdminSection() {
  const queryClient = useQueryClient();
  const claims = useAtomValue(claimsAtom);
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const orgId = claims?.orgId ?? '';

  const [extraPages, setExtraPages] = useState<OrgMember[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notOwner, setNotOwner] = useState(false);

  const query = useQuery({
    queryKey: ['org-roster', orgId],
    enabled: Boolean(isAuthenticated && sdkConfigured && orgId),
    queryFn: async () => {
      setNotOwner(false);
      setExtraPages([]);
      try {
        const page = await getSDK().auth.listMembers(orgId, { limit: 100 });
        setNextCursor(page.next_cursor ?? null);
        return page.members;
      } catch (err) {
        if (err instanceof APIError && err.status === 403) {
          setNotOwner(true);
          return [] as OrgMember[];
        }
        throw err;
      }
    },
  });

  const loadMore = useCallback(async () => {
    if (!orgId || !nextCursor) return;
    setLoadingMore(true);
    try {
      const page = await getSDK().auth.listMembers(orgId, {
        cursor: nextCursor,
        limit: 100,
      });
      setExtraPages((prev) => [...prev, ...page.members]);
      setNextCursor(page.next_cursor ?? null);
    } finally {
      setLoadingMore(false);
    }
  }, [orgId, nextCursor]);

  const canAdminister = query.isSuccess && !notOwner;

  const policyQuery = useQuery({
    queryKey: ['org-invite-policy', orgId],
    enabled: Boolean(canAdminister && orgId),
    queryFn: () => getSDK().auth.getInvitePolicy(orgId),
  });

  const [policyDraft, setPolicyDraft] = useState<InvitePolicy | null>(null);
  const [draftDomain, setDraftDomain] = useState('');
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [policySaved, setPolicySaved] = useState<string | null>(null);

  const stored = policyQuery.data ?? null;
  useEffect(() => {
    if (stored) setPolicyDraft(stored);
  }, [stored]);

  const setDraft = (next: InvitePolicy) => {
    setPolicyDraft(next);
    setPolicySaved(null);
  };

  const savePolicy = useCallback(async () => {
    if (!policyDraft) return;
    setSavingPolicy(true);
    setPolicyError(null);
    setPolicySaved(null);
    try {
      const saved = await getSDK().auth.setInvitePolicy(orgId, {
        email_invites: policyDraft.email_invites,
        email_allowed_domains: policyDraft.email_allowed_domains,
      });
      queryClient.setQueryData(['org-invite-policy', orgId], saved);
      setPolicyDraft(saved);
      setPolicySaved('Invite policy saved');
    } catch (err) {
      setPolicyError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingPolicy(false);
    }
  }, [orgId, policyDraft, queryClient]);

  const policyDirty =
    Boolean(policyDraft && stored) &&
    (policyDraft!.email_invites !== stored!.email_invites ||
      policyDraft!.email_allowed_domains.join(',') !== stored!.email_allowed_domains.join(','));

  const members = [...(query.data ?? []), ...extraPages];
  const errorMsg =
    query.error instanceof Error ? query.error.message : query.error ? String(query.error) : null;

  if (!isAuthenticated || !orgId) {
    return null;
  }

  return (
    <section className="space-y-4" data-testid="organization-admin-section">
      <div>
        <h2 className="text-title-2 font-semibold">Organization members</h2>
        <p className="text-body text-muted-foreground">
          Membership comes from Stytch; organization roles are LocalAgent grants.
        </p>
      </div>
      {canAdminister && policyDraft ? (
        <OrganizationInvitePolicyForm
          mode={policyDraft.email_invites}
          domains={policyDraft.email_allowed_domains}
          draftDomain={draftDomain}
          onModeChange={(mode: InviteMode) =>
            setDraft({ ...policyDraft, email_invites: mode })
          }
          onDraftDomainChange={setDraftDomain}
          onAddDomain={() => {
            const next = draftDomain.trim().toLowerCase();
            if (!next || policyDraft.email_allowed_domains.includes(next)) return;
            setDraft({
              ...policyDraft,
              email_allowed_domains: [...policyDraft.email_allowed_domains, next],
            });
            setDraftDomain('');
          }}
          onRemoveDomain={(domain: string) =>
            setDraft({
              ...policyDraft,
              email_allowed_domains: policyDraft.email_allowed_domains.filter(
                (d) => d !== domain,
              ),
            })
          }
          onSave={() => void savePolicy()}
          isLoading={policyQuery.isLoading}
          isSaving={savingPolicy}
          isDirty={policyDirty}
          error={
            policyError ??
            (policyQuery.error instanceof Error ? policyQuery.error.message : null)
          }
          success={policySaved}
        />
      ) : null}
      {!notOwner ? (
        <InviteMemberFormSection
          organizationId={orgId}
          onInvited={() => {
            void query.refetch();
          }}
        />
      ) : null}
      <OrganizationRoster
        members={members}
        isLoading={query.isLoading}
        error={notOwner ? null : errorMsg}
        notOwner={notOwner}
        nextCursor={nextCursor}
        onLoadMore={() => void loadMore()}
        loadingMore={loadingMore}
      />
    </section>
  );
}
